import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Popconfirm, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import {
    WebApiMemberDtoMemberDto,
    WebApiModelsMemberField,
    WebApiModelsCustomListField,
    WebApiCustomListsDtoCustomListItemDto,
    WebApiMemberDtoCustomListConnectionDto
} from '../../Repository/eventbookyapi';
import { TextService } from '../../Services/TextService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { CustomListFieldService } from '../../Services/CustomLifFieldService';
import CustomListEditModal from '../../Pages/CustomList/CustomListEditModal';
import CustomListAddModal from '../../Pages/CustomList/CustomListAddModal';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { LookupRelationType } from '../../Types/LookupRelationType';

interface MemberCustomListConnectionsProps {
    member: WebApiMemberDtoMemberDto;
    loadMember: () => void;
}

const MemberCustomListConnections: React.FC<MemberCustomListConnectionsProps> = ({ member, loadMember }) => {
    const GetText = TextService.GetText;

    const [memberFieldSettings, setMemberFieldSettings] = useState<WebApiModelsMemberField[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [modalField, setModalField] = useState<WebApiModelsMemberField | null>(null);
    const [modalCustomListId, setModalCustomListId] = useState<number | null>(null);
    const [customFieldsForModal, setCustomFieldsForModal] = useState<WebApiModelsCustomListField[]>([]);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        loadMemberFields();
    }, []);

    const loadMemberFields = async () => {
        try {
            setLoading(true);
            const fields = await MemberFieldService.GetMemberFields();
            // Filter to only lookup fields (uiControl === 4)
            const lookupFields = fields.filter(f => f.uiControl === 4);
            setMemberFieldSettings(lookupFields);
        } catch (error) {
            console.error('Error loading member fields:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (field: WebApiModelsMemberField, connection: any) => {
        setModalLoading(true);
        setModalField(field);
        setModalCustomListId(connection.customListId);

        try {
            const fields = await CustomListFieldService.GetCCustomListFields(connection.customListId) as WebApiModelsCustomListField[];
            setCustomFieldsForModal(fields);
            setEditItem(connection.item || { id: connection.customListItemId });
            setEditModalOpen(true);
        } catch (err) {
            console.error('Failed to load custom fields', err);
        } finally {
            setModalLoading(false);
        }
    };

    const handleAdd = async (field: WebApiModelsMemberField) => {
        if (!field.customListId) return;

        setModalLoading(true);
        setModalField(field);
        setModalCustomListId(field.customListId);

        try {
            const fields = await CustomListFieldService.GetCCustomListFields(field.customListId) as WebApiModelsCustomListField[];
            setCustomFieldsForModal(fields);
            setAddModalOpen(true);
        } catch (err) {
            console.error('Failed to load custom fields', err);
        } finally {
            setModalLoading(false);
        }
    };

    const handleDelete = async (connectionId: number) => {
        // TODO: Implement delete connection API call
        console.log('Delete connection:', connectionId);
        // After successful delete:
        // loadMember();
    };

    const handleModalSuccess = () => {
        setEditModalOpen(false);
        setAddModalOpen(false);
        loadMember();
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
            </div>
        );
    }

    if (memberFieldSettings.length === 0) {
        return null; // Don't show anything if there are no lookup fields
    }

    return (
        <>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {memberFieldSettings.map((field) => {
                    // Get all connections for this field (not just the first one)
                    const connections = member.customListConnections?.filter(
                        (c) => c.memberFieldId === field.id
                    ) || [];

                    // Check lookup relation type
                    const lookupType = field.lookupRelationType ?? LookupRelationType.None;

                    // Don't show if lookupRelationType is None
                    if (lookupType === LookupRelationType.None) {
                        return null;
                    }

                    // For One-to-One, hide "Add" button if a connection already exists
                    const showAddButton = lookupType !== LookupRelationType.OneToOne || connections.length === 0;

                    // Show edit/delete only for One-to-Many
                    const showEditDelete = lookupType === LookupRelationType.OneToMany;

                    return (
                        <Card
                            key={field.id}
                            size="small"
                            title={<>{field.nameAdmin || field.namePublic}</>}
                            extra={
                                showAddButton ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => handleAdd(field)}
                                        loading={modalLoading}
                                    >
                                        {GetText("Common_Add") || "Add"}
                                    </Button>
                                ) : null
                            }
                        >
                            {connections.length > 0 ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {connections.map((connection, index) => (
                                        <div key={connection.customListItemId || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>
                                                {connection.item?.fieldValues?.[0]?.fieldValue || connection.customListItemId}
                                            </span>
                                            {showEditDelete && (
                                                <Space size="small">
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        icon={<EditOutlined style={{ fontSize: "20px" }} />}
                                                        onClick={() => handleEdit(field, connection)}
                                                        loading={modalLoading}
                                                    />
                                                    {connection.customListItemId && (
                                                        <Popconfirm
                                                            title={GetText("Common_ConfirmDelete") || "Are you sure you want to delete this connection?"}
                                                            onConfirm={() => handleDelete(connection.customListItemId!)}
                                                            okText={GetText("Common_Yes") || "Yes"}
                                                            cancelText={GetText("Common_No") || "No"}
                                                        >
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                danger
                                                                icon={<DeleteOutlined style={{ fontSize: "20px" }} />}
                                                            />
                                                        </Popconfirm>
                                                    )}
                                                </Space>
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            ) : (
                                <span style={{ color: '#999' }}>
                                    {GetText("MemberDetails_NoConnection") || "No connection"}
                                </span>
                            )}
                        </Card>
                    );
                })}
            </Space>

            <CustomListEditModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSuccess={handleModalSuccess}
                customFields={customFieldsForModal}
                item={editItem!}
            />
            <CustomListAddModal
                open={addModalOpen}
                member={member}
                onClose={() => setAddModalOpen(false)}
                onSuccess={handleModalSuccess}
                customFields={customFieldsForModal}
                customListId={modalCustomListId || 1}
            />
        </>
    );
};

export default MemberCustomListConnections;
